import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { InviteForRater } from "~vue/api/generated/reflections";
import { onMounted, ref } from "vue";

export type InvitePayload = InviteForRater;

export interface UseInviteLoaderOptions {
    /**
     * If set, the loader redirects the browser to this URL when the invite's
     * `state === "responded"`. Use on rater entry pages so a re-visit lands
     * back on the "done" screen instead of letting the rater re-submit.
     */
    redirectIfResponded?: string;
    /**
     * If set, the loader redirects the browser to this URL when the cycle's
     * `cycle_state === "closed"`. Use on downstream pages (relationship,
     * question) so a direct-navigation attempt bounces back to the landing
     * page where the closed-cycle message is shown.
     */
    redirectIfCycleClosed?: string;
}

/**
 * Fetches the rater-facing view of an invite by token. All five rater pages
 * share the same load + error + "already submitted" handling — keep it here.
 */
export function useInviteLoader(token: string, options: UseInviteLoaderOptions = {}) {
    const invite = ref<InvitePayload | null>(null);
    const loading = ref(true);
    const loadError = ref<string | null>(null);

    async function load() {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/invites/${token}`).json();
        loading.value = false;
        if (fetchError.value) {
            loadError.value = (data.value as { error?: string })?.error || "Could not load invite.";
            return;
        }
        const payload = (data.value as { invite: InvitePayload }).invite;
        if (options.redirectIfCycleClosed && payload.cycle_state === "closed") {
            router.visit(options.redirectIfCycleClosed);
            return;
        }
        if (options.redirectIfResponded && payload.state === "responded") {
            router.visit(options.redirectIfResponded);
            return;
        }
        invite.value = payload;
    }

    onMounted(load);

    return { invite, loading, loadError, reload: load };
}
